<template>
  <q-page class="guia-sit-page">
    <div class="guia-sit-container">
      <!-- Loading state -->
      <div v-if="loading" class="guia-sit-loading">
        Cargando...
      </div>

      <!-- Not found -->
      <div v-else-if="!situation" class="guia-sit-empty">
        <q-icon name="warning" size="32px" style="color: var(--oa-gold-full);" />
        <p class="empty-text">Situacion no encontrada</p>
        <router-link :to="{ name: 'guia' }" class="back-link">Volver a la guia</router-link>
      </div>

      <!-- Situation loaded -->
      <template v-else>
        <!-- Header -->
        <div class="sit-header">
          <div class="sit-header-left">
            <router-link :to="{ name: 'guia' }" class="sit-back">
              <q-icon name="arrow_back" size="20px" />
            </router-link>
            <div>
              <div class="sit-title-row">
                <span class="sit-code">{{ situation.code }}</span>
                <h1 class="sit-title">{{ situation.title }}</h1>
              </div>
              <p v-if="situation.subtitle" class="sit-subtitle">{{ situation.subtitle }}</p>
            </div>
          </div>
          <div class="sit-header-right">
            <div class="sit-mode-toggle">
              <button
                class="mode-btn"
                :class="{ 'mode-btn-active': isLearning }"
                @click="setMode('learning')"
              >
                <q-icon name="menu_book" size="12px" /> Aprendizaje
              </button>
              <button
                class="mode-btn"
                :class="{ 'mode-btn-active': !isLearning }"
                @click="setMode('realtime')"
              >
                <q-icon name="bolt" size="12px" /> Tiempo Real
              </button>
            </div>
            <q-btn
              flat
              round
              dense
              icon="bookmark"
              style="color: var(--oa-gold-full);"
              @click="toggleBookmark"
            />
          </div>
        </div>

        <!-- Progress -->
        <div class="sit-progress-row">
          <q-linear-progress
            :value="progressPct / 100"
            color="amber"
            track-color="grey-9"
            rounded
            size="6px"
            style="flex: 1;"
          />
          <span class="sit-step-label">Paso {{ currentStep }}/{{ totalSteps }}</span>
        </div>

        <!-- Step dots -->
        <div class="sit-dots">
          <button
            v-for="s in situation.steps"
            :key="s.stepNumber"
            class="sit-dot"
            :class="{
              'dot-current': s.stepNumber === currentStep,
              'dot-past': s.stepNumber < currentStep,
              'dot-future': s.stepNumber > currentStep,
            }"
            @click="goToStep(s.stepNumber)"
          />
        </div>

        <!-- Current step -->
        <div v-if="step" class="sit-step-card" :class="{ 'step-alerta': step.stepType === 'alerta' }">
          <!-- Step header -->
          <div class="step-header">
            <div class="step-icon-wrap" :class="{ 'step-icon-alerta': step.stepType === 'alerta' }">
              <q-icon :name="STEP_ICONS[step.stepType] ?? 'arrow_forward'" :size="isLearning ? '16px' : '14px'" />
            </div>
            <div>
              <p class="step-title" :class="{ 'step-title-compact': !isLearning }">{{ step.title }}</p>
              <p class="step-type-label" :class="{ 'step-type-compact': !isLearning }">{{ step.stepType }}</p>
            </div>
          </div>

          <!-- Content -->
          <p v-if="step.content" class="step-content" :class="{ 'step-content-compact': !isLearning }">
            {{ step.content }}
          </p>

          <!-- Decision options -->
          <div v-if="step.stepType === 'decision' && step.options" class="step-options">
            <button
              v-for="(opt, i) in step.options"
              :key="i"
              class="step-option"
              :class="{ 'step-option-compact': !isLearning }"
              @click="handleOptionSelect(opt)"
            >
              <div class="step-option-content">
                <p class="step-option-label">{{ opt.label }}</p>
                <p v-if="opt.description" class="step-option-desc">{{ opt.description }}</p>
              </div>
              <q-icon name="chevron_right" size="16px" style="color: var(--oa-text-disabled);" />
            </button>
          </div>

          <!-- Script variants -->
          <div v-if="step.stepType === 'script' && step.scriptVariants" class="step-scripts">
            <div v-if="step.scriptVariants.length > 1" class="script-tabs">
              <button
                v-for="(sv, i) in step.scriptVariants"
                :key="i"
                class="script-tab"
                :class="{ 'script-tab-active': i === selectedScript }"
                @click="selectedScript = i"
              >
                {{ sv.context }}
              </button>
            </div>
            <div class="script-body">
              <p class="script-text" :class="{ 'script-text-compact': !isLearning }">
                &ldquo;{{ step.scriptVariants[selectedScript]?.script }}&rdquo;
              </p>
            </div>
          </div>

          <!-- Checklist -->
          <div v-if="step.stepType === 'checklist' && step.checklistItems" class="step-checklist">
            <q-checkbox
              v-for="(item, i) in step.checklistItems"
              :key="i"
              :model-value="checkedItems.has(i)"
              dark
              dense
              color="amber"
              class="step-checklist-item"
              @update:model-value="toggleCheckItem(i)"
            >
              <template #default>
                <span
                  :class="{ 'checklist-checked': checkedItems.has(i) }"
                  :style="{ fontSize: isLearning ? '14px' : '12px' }"
                >
                  {{ item }}
                </span>
              </template>
            </q-checkbox>
            <p v-if="step.checklistItems.length > 0" class="checklist-counter">
              {{ checkedItems.size }}/{{ step.checklistItems.length }} completados
            </p>
          </div>

          <!-- Alerta styling -->
          <div v-if="step.stepType === 'alerta'" class="step-alerta-body" :style="alertStyle">
            <q-icon :name="alertIcon" size="20px" />
            <p class="step-alerta-text">{{ step.content }}</p>
          </div>

          <!-- Temporizador -->
          <div v-if="step.stepType === 'temporizador'" class="step-timer">
            <q-circular-progress
              :value="timerProgress"
              size="80px"
              :thickness="0.15"
              color="amber"
              track-color="grey-8"
              class="step-timer-circle"
            >
              <span class="timer-text">{{ timerDisplay }}</span>
            </q-circular-progress>
            <q-btn
              v-if="!timerRunning"
              no-caps
              unelevated
              label="Iniciar"
              size="sm"
              style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 8px; margin-top: 8px;"
              @click="startTimer"
            />
          </div>

          <!-- Referencia -->
          <div v-if="step.stepType === 'referencia' && step.referenceCode" class="step-referencia">
            <q-badge
              :label="step.referenceCode"
              style="background: color-mix(in srgb, var(--oa-gold-full) 15%, transparent); color: var(--oa-gold-full); font-size: 11px; padding: 4px 10px; border-radius: 6px;"
            />
          </div>

          <!-- Learning content (Aprendizaje mode only) -->
          <div v-if="isLearning && step.learningContent" class="step-learning">
            <!-- Theory -->
            <div v-if="step.learningContent.theory" class="learning-block learning-theory">
              <div class="learning-block-header">
                <q-icon name="menu_book" size="16px" style="color: var(--oa-tier-sc);" />
                <p class="learning-block-title" style="color: var(--oa-tier-sc);">Por que hacemos esto</p>
              </div>
              <p class="learning-block-text">{{ step.learningContent.theory }}</p>
            </div>
            <!-- Examples -->
            <div v-if="step.learningContent.examples?.length" class="learning-block learning-examples">
              <div class="learning-block-header">
                <q-icon name="lightbulb" size="16px" style="color: var(--oa-tier-cc);" />
                <p class="learning-block-title" style="color: var(--oa-tier-cc);">Ejemplos reales</p>
              </div>
              <ul class="learning-block-list">
                <li v-for="(ex, i) in step.learningContent.examples" :key="i">
                  <span class="learning-bullet" style="color: var(--oa-tier-cc);">-</span> {{ ex }}
                </li>
              </ul>
            </div>
            <!-- Tips -->
            <div v-if="step.learningContent.tips?.length" class="learning-block learning-tips">
              <div class="learning-block-header">
                <q-icon name="star" size="16px" style="color: var(--oa-gold-full);" />
                <p class="learning-block-title" style="color: var(--oa-gold-full);">Tips de pro</p>
              </div>
              <ul class="learning-block-list">
                <li v-for="(tip, i) in step.learningContent.tips" :key="i">
                  <span class="learning-bullet" style="color: var(--oa-gold-dim);">-</span> {{ tip }}
                </li>
              </ul>
            </div>
            <!-- Common errors -->
            <div v-if="step.learningContent.commonErrors?.length" class="learning-block learning-errors">
              <div class="learning-block-header">
                <q-icon name="report" size="16px" style="color: #ef4444;" />
                <p class="learning-block-title" style="color: #ef4444;">Errores comunes</p>
              </div>
              <ul class="learning-block-list">
                <li v-for="(err, i) in step.learningContent.commonErrors" :key="i">
                  <span class="learning-bullet" style="color: #ef4444;">-</span> {{ err }}
                </li>
              </ul>
            </div>
          </div>
        </div>

        <!-- Navigation -->
        <div class="sit-nav">
          <q-btn
            no-caps
            flat
            icon="chevron_left"
            label="Anterior"
            :disabled="currentStep === 1"
            style="color: var(--oa-text-muted); border-radius: 12px;"
            @click="goToStep(currentStep - 1)"
          />
          <q-btn
            v-if="step?.stepType !== 'decision'"
            no-caps
            unelevated
            :icon-right="currentStep === totalSteps ? 'check_circle' : 'chevron_right'"
            :label="currentStep === totalSteps ? 'Completado' : 'Siguiente'"
            :disabled="currentStep === totalSteps && !step?.nextStepDefault"
            :style="{
              background: currentStep === totalSteps ? 'var(--oa-success)' : 'var(--oa-gold-full)',
              color: 'var(--color-marble-cream)',
              fontWeight: 600,
              borderRadius: '12px',
            }"
            @click="handleNext"
          />
        </div>

        <!-- Exam CTA (Learning mode, last step) -->
        <div v-if="isLearning && currentStep === totalSteps" class="sit-exam-cta">
          <q-icon name="school" size="32px" style="color: var(--oa-gold-full);" />
          <p class="exam-cta-title">Dominas esta situacion?</p>
          <p class="exam-cta-desc">Rendi el examen para certificar tu conocimiento</p>
          <q-btn
            no-caps
            unelevated
            label="Rendir examen"
            style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 12px; padding: 8px 20px;"
            @click="startExam"
          />
        </div>

        <!-- Exam dialog -->
        <q-dialog v-model="examOpen" persistent>
          <q-card dark class="exam-dialog">
            <q-card-section>
              <div class="exam-dialog-header">
                <h3 class="exam-dialog-title">Examen: {{ situation.title }}</h3>
                <q-btn flat round dense icon="close" style="color: var(--oa-text-muted);" @click="examOpen = false" />
              </div>
            </q-card-section>

            <!-- Exam: questions -->
            <q-card-section v-if="!examResults" class="exam-questions">
              <div v-for="q in examQuestions" :key="q.id" class="exam-question">
                <p class="exam-q-text">{{ q.question }}</p>
                <div class="exam-options">
                  <button
                    v-for="opt in q.options"
                    :key="opt.id"
                    class="exam-option"
                    :class="{ 'exam-option-selected': examAnswers[q.id] === opt.id }"
                    @click="examAnswers[q.id] = opt.id"
                  >
                    {{ opt.text }}
                  </button>
                </div>
              </div>
              <q-btn
                no-caps
                unelevated
                label="Enviar respuestas"
                :disabled="Object.keys(examAnswers).length < examQuestions.length || examSubmitting"
                style="background: var(--oa-gold-full); color: var(--color-marble-cream); font-weight: 600; border-radius: 12px; width: 100%; margin-top: 16px;"
                @click="submitExam"
              />
            </q-card-section>

            <!-- Exam: results -->
            <q-card-section v-else class="exam-results">
              <div class="exam-result-header">
                <q-icon
                  :name="examResults.passed ? 'check_circle' : 'cancel'"
                  size="40px"
                  :style="{ color: examResults.passed ? 'var(--oa-success)' : '#ef4444' }"
                />
                <p class="exam-result-score">{{ examResults.scorePct }}%</p>
                <q-badge
                  :label="examResults.passed ? 'Aprobado' : 'Desaprobado'"
                  :style="{
                    background: examResults.passed
                      ? 'color-mix(in srgb, var(--oa-success) 15%, transparent)'
                      : 'color-mix(in srgb, #ef4444 15%, transparent)',
                    color: examResults.passed ? 'var(--oa-success)' : '#ef4444',
                    fontSize: '12px',
                    padding: '4px 12px',
                    borderRadius: '8px',
                  }"
                />
                <p class="exam-result-detail">
                  {{ examResults.correctAnswers }}/{{ examResults.totalQuestions }} correctas
                </p>
                <p v-if="examResults.auraEarned" class="exam-result-aura">
                  +{{ examResults.auraEarned }} AURA
                </p>
              </div>

              <!-- Per-question feedback -->
              <div v-if="examResults.questionsDetail" class="exam-detail-list">
                <div
                  v-for="qd in examResults.questionsDetail"
                  :key="qd.questionId"
                  class="exam-detail-item"
                  :style="{
                    background: qd.correct
                      ? 'color-mix(in srgb, var(--oa-success) 6%, transparent)'
                      : 'color-mix(in srgb, #ef4444 6%, transparent)',
                    borderLeft: `3px solid ${qd.correct ? 'var(--oa-success)' : '#ef4444'}`,
                  }"
                >
                  <q-icon
                    :name="qd.correct ? 'check' : 'close'"
                    size="14px"
                    :style="{ color: qd.correct ? 'var(--oa-success)' : '#ef4444' }"
                  />
                  <span class="exam-detail-text">{{ qd.explanation || (qd.correct ? 'Correcto' : 'Incorrecto') }}</span>
                </div>
              </div>

              <q-btn
                no-caps
                unelevated
                label="Cerrar"
                style="background: var(--oa-bg-3); color: var(--oa-text-primary); font-weight: 500; border-radius: 12px; width: 100%; margin-top: 16px;"
                @click="examOpen = false"
              />
            </q-card-section>
          </q-card>
        </q-dialog>
      </template>
    </div>
  </q-page>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, onBeforeUnmount } from 'vue';
import { useRoute } from 'vue-router';
import { api } from 'boot/axios';

// ---------- Types ----------
interface StepOption {
  label: string;
  description?: string;
  nextStep?: number;
}

interface ScriptVariant {
  context: string;
  script: string;
}

interface LearningContent {
  theory?: string;
  examples?: string[];
  tips?: string[];
  commonErrors?: string[];
}

interface Step {
  stepNumber: number;
  stepType: string;
  title: string;
  content?: string;
  options?: StepOption[];
  scriptVariants?: ScriptVariant[];
  checklistItems?: string[];
  alertLevel?: string;
  timerSeconds?: number;
  referenceCode?: string;
  nextStepDefault?: number;
  learningContent?: LearningContent;
}

interface SituationData {
  id: string;
  code: string;
  title: string;
  subtitle?: string;
  triggerQuestion?: string;
  category: string;
  difficulty: string;
  steps: Step[];
}

interface ExamQuestion {
  id: string;
  question: string;
  options: { id: string; text: string }[];
}

interface ExamResults {
  scorePct: number;
  passed: boolean;
  correctAnswers: number;
  totalQuestions: number;
  auraEarned?: number;
  questionsDetail?: { questionId: string; correct: boolean; explanation?: string }[];
}

// ---------- Constants ----------
const STEP_ICONS: Record<string, string> = {
  accion: 'arrow_forward',
  decision: 'fork_right',
  script: 'chat',
  checklist: 'checklist',
  alerta: 'warning',
  referencia: 'link',
  temporizador: 'timer',
};

const ALERT_COLORS: Record<string, { bg: string; color: string; icon: string }> = {
  info: { bg: 'color-mix(in srgb, var(--oa-tier-sc) 8%, transparent)', color: 'var(--oa-tier-sc)', icon: 'info' },
  warning: { bg: 'color-mix(in srgb, var(--oa-gold-full) 8%, transparent)', color: 'var(--oa-gold-full)', icon: 'warning' },
  critical: { bg: 'color-mix(in srgb, #ef4444 8%, transparent)', color: '#ef4444', icon: 'error' },
  rojo: { bg: 'color-mix(in srgb, #ef4444 8%, transparent)', color: '#ef4444', icon: 'error' },
  amarillo: { bg: 'color-mix(in srgb, var(--oa-gold-full) 8%, transparent)', color: 'var(--oa-gold-full)', icon: 'warning' },
  verde: { bg: 'color-mix(in srgb, var(--oa-success) 8%, transparent)', color: 'var(--oa-success)', icon: 'check_circle' },
};

// ---------- Route ----------
const route = useRoute();
const situationId = computed(() => route.params.situationId as string);

// ---------- State ----------
const loading = ref(true);
const situation = ref<SituationData | null>(null);
const currentStep = ref(1);
const checkedItems = ref<Set<number>>(new Set());
const selectedScript = ref(0);
const viewMode = ref<'learning' | 'realtime'>('learning');

// Timer
const timerRemaining = ref(0);
const timerTotal = ref(0);
const timerRunning = ref(false);
let timerInterval: ReturnType<typeof setInterval> | null = null;

// Exam
const examOpen = ref(false);
const examQuestions = ref<ExamQuestion[]>([]);
const examAnswers = ref<Record<string, string>>({});
const examResults = ref<ExamResults | null>(null);
const examSubmitting = ref(false);

// ---------- Computed ----------
const isLearning = computed(() => viewMode.value === 'learning');
const totalSteps = computed(() => situation.value?.steps.length ?? 0);
const progressPct = computed(() => totalSteps.value > 0 ? Math.round((currentStep.value / totalSteps.value) * 100) : 0);
const step = computed(() => situation.value?.steps.find((s) => s.stepNumber === currentStep.value));

const alertStyle = computed(() => {
  const level = step.value?.alertLevel ?? 'warning';
  const ac = ALERT_COLORS[level] ?? ALERT_COLORS['warning'];
  return { background: ac.bg, color: ac.color };
});

const alertIcon = computed(() => {
  const level = step.value?.alertLevel ?? 'warning';
  return (ALERT_COLORS[level] ?? ALERT_COLORS['warning']).icon;
});

const timerProgress = computed(() => {
  if (timerTotal.value <= 0) return 0;
  return ((timerTotal.value - timerRemaining.value) / timerTotal.value) * 100;
});

const timerDisplay = computed(() => {
  const mins = Math.floor(timerRemaining.value / 60);
  const secs = timerRemaining.value % 60;
  return `${mins}:${secs.toString().padStart(2, '0')}`;
});

// ---------- Navigation ----------
function goToStep(n: number) {
  if (n >= 1 && n <= totalSteps.value) {
    currentStep.value = n;
    checkedItems.value = new Set();
    selectedScript.value = 0;
    stopTimer();
  }
}

function handleNext() {
  if (step.value?.nextStepDefault) {
    goToStep(step.value.nextStepDefault);
  } else if (currentStep.value < totalSteps.value) {
    goToStep(currentStep.value + 1);
  }
}

function handleOptionSelect(opt: StepOption) {
  if (opt.nextStep) {
    goToStep(opt.nextStep);
  } else {
    handleNext();
  }
}

function toggleCheckItem(i: number) {
  const next = new Set(checkedItems.value);
  if (next.has(i)) next.delete(i);
  else next.add(i);
  checkedItems.value = next;
}

// ---------- Mode ----------
function setMode(mode: 'learning' | 'realtime') {
  viewMode.value = mode;
  try { localStorage.setItem('guide-view-mode', mode); } catch { /* noop */ }
}

// ---------- Timer ----------
function startTimer() {
  const secs = step.value?.timerSeconds ?? 0;
  if (secs <= 0) return;
  timerTotal.value = secs;
  timerRemaining.value = secs;
  timerRunning.value = true;
  timerInterval = setInterval(() => {
    timerRemaining.value--;
    if (timerRemaining.value <= 0) {
      stopTimer();
      handleNext();
    }
  }, 1000);
}

function stopTimer() {
  timerRunning.value = false;
  if (timerInterval) {
    clearInterval(timerInterval);
    timerInterval = null;
  }
}

// ---------- Bookmark ----------
async function toggleBookmark() {
  try {
    await api.post(`/guide/${situationId.value}/bookmark`);
  } catch {
    // silent
  }
}

// ---------- Exam ----------
async function startExam() {
  examResults.value = null;
  examAnswers.value = {};
  examSubmitting.value = false;
  try {
    const { data } = await api.post(`/guide/${situationId.value}/exam/start`);
    examQuestions.value = data.questions;
    examOpen.value = true;
  } catch {
    // silent
  }
}

async function submitExam() {
  examSubmitting.value = true;
  try {
    const { data } = await api.post(`/guide/${situationId.value}/exam/submit`, {
      answers: examAnswers.value,
    });
    examResults.value = data;
  } catch {
    // silent
  } finally {
    examSubmitting.value = false;
  }
}

// ---------- Data Fetching ----------
async function fetchSituation() {
  loading.value = true;
  try {
    const { data } = await api.get(`/guide/${situationId.value}`);
    situation.value = data;
    currentStep.value = 1;
  } catch {
    situation.value = null;
  } finally {
    loading.value = false;
  }
}

// ---------- Lifecycle ----------
onMounted(() => {
  try {
    const stored = localStorage.getItem('guide-view-mode');
    if (stored === 'learning' || stored === 'realtime') viewMode.value = stored;
  } catch { /* noop */ }

  fetchSituation();
});

onBeforeUnmount(() => {
  stopTimer();
});
</script>

<style scoped>
.guia-sit-page {
  padding: 24px;
}

.guia-sit-container {
  max-width: 680px;
  margin: 0 auto;
}

.guia-sit-loading {
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  font-size: 14px;
  color: var(--oa-text-muted);
}

.guia-sit-empty {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  min-height: 60vh;
  gap: 12px;
}

.empty-text {
  font-size: 14px;
  color: var(--oa-text-muted);
}

.back-link {
  font-size: 12px;
  color: var(--oa-gold-full);
  text-decoration: none;
}

/* Header */
.sit-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 16px;
  gap: 12px;
}

.sit-header-left {
  display: flex;
  align-items: center;
  gap: 12px;
  min-width: 0;
}

.sit-back {
  padding: 8px;
  border-radius: 12px;
  color: var(--oa-text-muted);
  text-decoration: none;
  transition: background 0.2s;
  flex-shrink: 0;
}

.sit-back:hover {
  background: var(--oa-bg-3);
}

.sit-title-row {
  display: flex;
  align-items: center;
  gap: 8px;
}

.sit-code {
  font-size: 10px;
  font-family: monospace;
  color: var(--oa-text-disabled);
  flex-shrink: 0;
}

.sit-title {
  font-size: 18px;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.sit-subtitle {
  font-size: 12px;
  color: var(--oa-text-disabled);
  margin: 0;
}

.sit-header-right {
  display: flex;
  align-items: center;
  gap: 8px;
  flex-shrink: 0;
}

.sit-mode-toggle {
  display: flex;
  align-items: center;
  background: var(--oa-bg-3);
  border-radius: 9999px;
  padding: 2px;
}

.mode-btn {
  display: flex;
  align-items: center;
  gap: 4px;
  padding: 4px 10px;
  border-radius: 9999px;
  font-size: 10px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: transparent;
  color: var(--oa-text-muted);
  font-family: inherit;
}

.mode-btn-active {
  background: var(--oa-gold-full);
  color: var(--color-marble-cream);
}

/* Progress */
.sit-progress-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 12px;
}

.sit-step-label {
  font-size: 10px;
  color: var(--oa-text-disabled);
  flex-shrink: 0;
}

/* Dots */
.sit-dots {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
  margin-bottom: 16px;
}

.sit-dot {
  width: 8px;
  height: 8px;
  border-radius: 9999px;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  padding: 0;
}

.dot-current {
  width: 24px;
  background: var(--oa-gold-full);
}

.dot-past {
  background: color-mix(in srgb, var(--oa-gold-full) 40%, transparent);
}

.dot-future {
  background: var(--oa-bg-3);
}

/* Step card */
.sit-step-card {
  background: var(--oa-bg-2);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  padding: 24px;
  margin-bottom: 16px;
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.sit-step-card.step-alerta {
  border-color: currentColor;
}

.step-header {
  display: flex;
  align-items: center;
  gap: 12px;
}

.step-icon-wrap {
  width: 32px;
  height: 32px;
  border-radius: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: color-mix(in srgb, var(--oa-gold-full) 10%, transparent);
  color: var(--oa-gold-full);
  flex-shrink: 0;
}

.step-icon-alerta {
  background: color-mix(in srgb, var(--oa-bg-2) 60%, transparent);
}

.step-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.step-title-compact {
  font-size: 12px;
}

.step-type-label {
  font-size: 10px;
  color: var(--oa-text-disabled);
  text-transform: capitalize;
  margin: 0;
}

.step-type-compact {
  font-size: 9px;
}

.step-content {
  font-size: 14px;
  color: var(--oa-text-secondary);
  line-height: 1.6;
  margin: 0;
}

.step-content-compact {
  font-size: 12px;
}

/* Decision options */
.step-options {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.step-option {
  display: flex;
  align-items: center;
  justify-content: space-between;
  width: 100%;
  text-align: left;
  padding: 12px 16px;
  border-radius: 12px;
  border: 1px solid var(--oa-border);
  background: transparent;
  cursor: pointer;
  transition: all 0.2s;
  font-family: inherit;
}

.step-option:hover {
  border-color: color-mix(in srgb, var(--oa-gold-full) 40%, transparent);
  background: color-mix(in srgb, var(--oa-gold-full) 5%, transparent);
}

.step-option-compact {
  padding: 8px 12px;
}

.step-option-content {
  flex: 1;
  min-width: 0;
}

.step-option-label {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.step-option-desc {
  font-size: 10px;
  color: var(--oa-text-disabled);
  margin: 4px 0 0;
}

/* Script */
.step-scripts {
  display: flex;
  flex-direction: column;
  gap: 12px;
}

.script-tabs {
  display: flex;
  gap: 6px;
}

.script-tab {
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 10px;
  font-weight: 500;
  border: none;
  cursor: pointer;
  transition: all 0.2s;
  background: var(--oa-bg-3);
  color: var(--oa-text-muted);
  font-family: inherit;
}

.script-tab-active {
  background: var(--oa-gold-full);
  color: var(--color-marble-cream);
}

.script-body {
  background: color-mix(in srgb, var(--oa-bg-2) 50%, transparent);
  border-radius: 12px;
  padding: 16px;
  border-left: 3px solid color-mix(in srgb, var(--oa-gold-full) 40%, transparent);
}

.script-text {
  font-size: 14px;
  font-style: italic;
  color: var(--oa-text-primary);
  line-height: 1.6;
  margin: 0;
}

.script-text-compact {
  font-size: 12px;
}

/* Checklist */
.step-checklist {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.step-checklist-item {
  margin: 0;
}

.checklist-checked {
  text-decoration: line-through;
  color: var(--oa-text-disabled);
}

.checklist-counter {
  font-size: 10px;
  color: var(--oa-text-disabled);
  margin: 0;
}

/* Alerta */
.step-alerta-body {
  display: flex;
  align-items: flex-start;
  gap: 12px;
  padding: 16px;
  border-radius: 12px;
}

.step-alerta-text {
  font-size: 14px;
  line-height: 1.6;
  margin: 0;
}

/* Timer */
.step-timer {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
  padding: 16px 0;
}

.timer-text {
  font-size: 16px;
  font-weight: 700;
  font-family: monospace;
  color: var(--oa-text-primary);
}

/* Referencia */
.step-referencia {
  padding: 8px 0;
}

/* Learning content */
.step-learning {
  display: flex;
  flex-direction: column;
  gap: 12px;
  border-top: 1px solid var(--oa-border);
  padding-top: 16px;
  margin-top: 4px;
}

.learning-block {
  border-radius: 12px;
  padding: 16px;
  background: var(--oa-bg-3);
  border: 1px solid var(--oa-border);
}

.learning-errors {
  background: color-mix(in srgb, #ef4444 4%, var(--oa-bg-3));
  border: 1px solid color-mix(in srgb, #ef4444 12%, var(--oa-border));
}

.learning-block-header {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-bottom: 8px;
}

.learning-block-title {
  font-size: 12px;
  font-weight: 600;
  margin: 0;
}

.learning-block-text {
  font-size: 14px;
  color: var(--oa-text-secondary);
  line-height: 1.6;
  margin: 0;
}

.learning-block-list {
  list-style: none;
  padding: 0;
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: 6px;
}

.learning-block-list li {
  font-size: 14px;
  color: var(--oa-text-secondary);
  line-height: 1.6;
  display: flex;
  gap: 8px;
}

.learning-bullet {
  flex-shrink: 0;
}

/* Navigation */
.sit-nav {
  display: flex;
  align-items: center;
  justify-content: space-between;
  margin-bottom: 16px;
}

/* Exam CTA */
.sit-exam-cta {
  background: var(--oa-bg-2);
  border-radius: 16px;
  border: 1px solid color-mix(in srgb, var(--oa-gold-full) 20%, transparent);
  padding: 20px;
  text-align: center;
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.exam-cta-title {
  font-size: 14px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.exam-cta-desc {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 0;
}

/* Exam dialog */
.exam-dialog {
  background: var(--oa-bg-1);
  border: 1px solid var(--oa-border);
  border-radius: 16px;
  max-width: 520px;
  width: 100%;
}

.exam-dialog-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.exam-dialog-title {
  font-size: 16px;
  font-weight: 600;
  color: var(--oa-text-primary);
  margin: 0;
}

.exam-questions {
  display: flex;
  flex-direction: column;
  gap: 20px;
  max-height: 60vh;
  overflow-y: auto;
}

.exam-question {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.exam-q-text {
  font-size: 14px;
  font-weight: 500;
  color: var(--oa-text-primary);
  margin: 0;
}

.exam-options {
  display: flex;
  flex-direction: column;
  gap: 4px;
}

.exam-option {
  width: 100%;
  text-align: left;
  padding: 10px 14px;
  border-radius: 8px;
  border: 1px solid var(--oa-border);
  background: transparent;
  color: var(--oa-text-secondary);
  font-size: 13px;
  cursor: pointer;
  transition: all 0.15s;
  font-family: inherit;
}

.exam-option:hover {
  border-color: var(--oa-gold-dim);
}

.exam-option-selected {
  border-color: var(--oa-gold-full);
  background: color-mix(in srgb, var(--oa-gold-full) 8%, transparent);
  color: var(--oa-text-primary);
}

/* Exam results */
.exam-results {
  display: flex;
  flex-direction: column;
  gap: 16px;
}

.exam-result-header {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
}

.exam-result-score {
  font-size: 32px;
  font-weight: 700;
  color: var(--oa-text-primary);
  margin: 0;
}

.exam-result-detail {
  font-size: 12px;
  color: var(--oa-text-muted);
  margin: 0;
}

.exam-result-aura {
  font-size: 14px;
  font-weight: 600;
  color: var(--oa-gold-full);
  margin: 0;
}

.exam-detail-list {
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-height: 200px;
  overflow-y: auto;
}

.exam-detail-item {
  display: flex;
  align-items: flex-start;
  gap: 8px;
  padding: 8px 12px;
  border-radius: 6px;
}

.exam-detail-text {
  font-size: 12px;
  color: var(--oa-text-secondary);
}

@media (max-width: 640px) {
  .sit-header {
    flex-direction: column;
  }

  .sit-header-right {
    width: 100%;
    justify-content: space-between;
  }
}
</style>
